import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import DashboardLayout from "../layout/DashboardLayout";
import "./ChatAnalyticsPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Session {
  session_id: string;
  guest_name: string | null;
  guest_phone: string | null;
  source: string;
  status: string;
  unread: number;
  ai_enabled: boolean | null;
  handoff_reason: string | null;
  handoff_briefing: string | null;
  handoff_briefing_at: string | null;
  created_at: string;
  updated_at: string;
}

const COLORS = ["#3b82f6", "#10b981", "#ef4444", "#f59e0b", "#8b5cf6", "#06b6d4"];

function fmtDay(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function minsAgo(iso: string) {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m waiting`;
  return `${Math.floor(m / 60)}h ${m % 60}m waiting`;
}

function StatCard({ value, label, variant }: { value: string | number; label: string; variant?: string }) {
  return (
    <div className={`chat-stat-card${variant ? " " + variant : ""}`}>
      <div className="chat-stat-value">{value ?? "—"}</div>
      <div className="chat-stat-label">{label}</div>
    </div>
  );
}

// ── Compute daily trend from sessions (last N days) ──────────────────────
function buildDailyTrend(sessions: Session[], days = 14) {
  const cutoff = Date.now() - days * 86400000;
  const map: Record<string, { day: string; total: number; handoffs: number }> = {};

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    map[key] = { day: key, total: 0, handoffs: 0 };
  }

  sessions.forEach(s => {
    const ts = new Date(s.created_at).getTime();
    if (ts < cutoff) return;
    const key = s.created_at.slice(0, 10);
    if (!map[key]) return;
    map[key].total++;
    if (s.status === "needs_attention" || s.handoff_reason) map[key].handoffs++;
  });

  return Object.values(map);
}

// ── Group count by a field ────────────────────────────────────────────────
function countBy(sessions: Session[], fn: (s: Session) => string) {
  const map: Record<string, number> = {};
  sessions.forEach(s => { const k = fn(s) || "unknown"; map[k] = (map[k] || 0) + 1; });
  return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

export default function ChatAnalyticsPage() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState<"overview"|"handoff"|"trends"|"sources">("overview");

  useEffect(() => {
    axios.get(`${API}/api/chat/sessions`)
      .then(r => setSessions(r.data || []))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Computed stats ─────────────────────────────────────────────────────
  const total      = sessions.length;
  const open       = sessions.filter(s => s.status === "open").length;
  const resolved   = sessions.filter(s => s.status === "resolved").length;
  const attention  = sessions.filter(s => s.status === "needs_attention").length;
  const handoffRate = total > 0 ? ((attention / total) * 100).toFixed(1) : "0";
  const totalUnread = sessions.reduce((n, s) => n + (s.unread || 0), 0);
  const aiOff      = sessions.filter(s => s.ai_enabled === false).length;

  const backlog    = sessions.filter(s => s.status === "needs_attention")
    .sort((a, b) => new Date(a.handoff_briefing_at || a.updated_at).getTime() - new Date(b.handoff_briefing_at || b.updated_at).getTime());

  const dailyTrend  = buildDailyTrend(sessions);
  const bySrc       = countBy(sessions, s => s.source);
  const byReason    = countBy(sessions.filter(s => s.handoff_reason), s => s.handoff_reason!);
  const byStatus    = [
    { name: "Open",       value: open },
    { name: "Resolved",   value: resolved },
    { name: "Needs Attn", value: attention },
  ].filter(d => d.value > 0);

  const recentWeek  = sessions.filter(s => new Date(s.created_at).getTime() > Date.now() - 7 * 86400000);
  const whatsapp7d  = recentWeek.filter(s => s.source === "whatsapp").length;
  const web7d       = recentWeek.filter(s => s.source === "web").length;

  return (
    <DashboardLayout>
      <div className="chat-analytics-page">
        <div className="chat-analytics-header">
          <div>
            <h2 className="chat-analytics-title">Chat Analytics</h2>
            <p className="chat-analytics-subtitle">Computed from {total} sessions</p>
          </div>
        </div>

        <div className="chat-analytics-tabs">
          {(["overview","handoff","trends","sources"] as const).map(t => (
            <button key={t} className={`chat-analytics-tab${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === "handoff" && attention > 0 && (
                <span className="chat-event-badge complaint" style={{ marginLeft: 6, padding: "1px 6px", fontSize: 10 }}>{attention}</span>
              )}
            </button>
          ))}
        </div>

        {loading && <div className="chat-analytics-loading">Loading…</div>}
        {error   && <div className="chat-analytics-empty">Error: {error}</div>}

        {!loading && !error && (
          <>
            {/* ── OVERVIEW ── */}
            {tab === "overview" && (
              <>
                <div className="chat-stat-grid">
                  <StatCard value={total}          label="Total Sessions"       variant="info" />
                  <StatCard value={open}           label="Open"                 variant="success" />
                  <StatCard value={attention}      label="Need Attention"       variant={attention > 0 ? "danger" : undefined} />
                  <StatCard value={`${handoffRate}%`} label="Handoff Rate"      variant={Number(handoffRate) > 10 ? "danger" : "warning"} />
                </div>

                <div className="chat-stat-grid" style={{ marginTop: 12 }}>
                  <StatCard value={resolved}   label="Resolved" />
                  <StatCard value={totalUnread} label="Total Unread Messages" variant={totalUnread > 0 ? "warning" : undefined} />
                  <StatCard value={aiOff}       label="AI Disabled Guests" />
                  <StatCard value={recentWeek.length} label="New This Week" variant="info" />
                </div>

                <div className="chat-analytics-section">
                  <div className="chat-analytics-section-title">Session Status Distribution</div>
                  <div className="chat-analytics-grid-2">
                    <div className="chat-chart-card">
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name}: ${value}`}>
                            {byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#112240", border: "1px solid #1e3a5f", color: "#e2e8f0" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="chat-analytics-table-wrap">
                      <table className="chat-analytics-table">
                        <thead><tr><th>Status</th><th>Count</th><th>%</th></tr></thead>
                        <tbody>
                          {byStatus.map(r => (
                            <tr key={r.name}>
                              <td>{r.name}</td>
                              <td>{r.value}</td>
                              <td>{total > 0 ? ((r.value / total) * 100).toFixed(1) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── HANDOFF ── */}
            {tab === "handoff" && (
              <>
                <div className="chat-analytics-section">
                  <div className="chat-analytics-section-title">
                    Handoff Backlog
                    {backlog.length > 0 && (
                      <span className="chat-event-badge complaint" style={{ marginLeft: 10 }}>{backlog.length} waiting</span>
                    )}
                  </div>
                  {backlog.length === 0 ? (
                    <div className="chat-handoff-empty">No conversations currently waiting for attention.</div>
                  ) : (
                    backlog.map(s => (
                      <div key={s.session_id} className="chat-handoff-card">
                        <div className="chat-handoff-name">{s.guest_name || "Guest"}</div>
                        <div className="chat-handoff-meta">
                          <span>{s.guest_phone || s.session_id}</span>
                          {s.handoff_briefing_at && <span> · {minsAgo(s.handoff_briefing_at)}</span>}
                        </div>
                        {s.handoff_reason && (
                          <span className={`chat-event-badge ${
                            s.handoff_reason.includes("complaint") ? "complaint" :
                            s.handoff_reason.includes("loop")      ? "loop"      : "mood"
                          }`}>{s.handoff_reason.replace(/_/g, " ")}</span>
                        )}
                        {s.handoff_briefing && (
                          <div className="chat-handoff-briefing">{s.handoff_briefing}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {byReason.length > 0 && (
                  <div className="chat-analytics-section">
                    <div className="chat-analytics-section-title">Handoff Reasons</div>
                    <div className="chat-analytics-table-wrap">
                      <table className="chat-analytics-table">
                        <thead><tr><th>Reason</th><th>Count</th></tr></thead>
                        <tbody>
                          {byReason.map(r => (
                            <tr key={r.name}>
                              <td>{r.name.replace(/_/g, " ")}</td>
                              <td><span className="chat-event-badge complaint">{r.value}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── TRENDS ── */}
            {tab === "trends" && (
              <div className="chat-analytics-section">
                <div className="chat-analytics-section-title">Sessions Created — last 14 days</div>
                <div className="chat-chart-card">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={dailyTrend} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                      <XAxis dataKey="day" tickFormatter={fmtDay} stroke="#94a3b8" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#112240", border: "1px solid #1e3a5f", color: "#e2e8f0" }}
                        labelFormatter={fmtDay}
                      />
                      <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
                      <Line type="monotone" dataKey="total"    stroke="#3b82f6" strokeWidth={2} dot={false} name="Sessions" />
                      <Line type="monotone" dataKey="handoffs" stroke="#ef4444" strokeWidth={2} dot={false} name="Handoffs" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── SOURCES ── */}
            {tab === "sources" && (
              <>
                <div className="chat-stat-grid" style={{ marginTop: 8 }}>
                  <StatCard value={whatsapp7d} label="WhatsApp (7 days)" variant="success" />
                  <StatCard value={web7d}      label="Web (7 days)"      variant="info" />
                  <StatCard value={sessions.filter(s => s.source === "whatsapp").length} label="WhatsApp (all time)" />
                  <StatCard value={sessions.filter(s => s.source === "web").length}      label="Web (all time)" />
                </div>

                <div className="chat-analytics-section">
                  <div className="chat-analytics-section-title">Source Breakdown</div>
                  <div className="chat-analytics-grid-2">
                    <div className="chat-chart-card">
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={bySrc} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                            label={({ name, value }) => `${name}: ${value}`}>
                            {bySrc.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#112240", border: "1px solid #1e3a5f", color: "#e2e8f0" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="chat-analytics-table-wrap">
                      <table className="chat-analytics-table">
                        <thead><tr><th>Source</th><th>Sessions</th><th>%</th></tr></thead>
                        <tbody>
                          {bySrc.map(r => (
                            <tr key={r.name}>
                              <td>{r.name}</td>
                              <td>{r.value}</td>
                              <td>{total > 0 ? ((r.value / total) * 100).toFixed(1) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
